import React, { useState, useEffect } from "react";
import { getDictationList, getDictationStatusCounts } from "apiHandler/actions";
import { useUserData } from "components/customHooks/userDataContext";
import AdvanceSearchDictation from "components/modals/AdvanceSearchDictation";
import DictationsList from "components/scribe/DictationsList";
import UploadDictationModal from "components/modals/UploadDictation";
import useSnackbar from "components/customHooks/useSnackbar";
import { Select, MenuItem, FormControl, Box, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  setDosFrom,
  setDosTo,
  setStatus,
} from "../../redux/reducers/dictation";

const formControlStyles = {
  marginLeft: 1,
  marginRight: 1,
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#d91a5f", // Default border color
    },
    "&:hover fieldset": {
      borderColor: "#d91a5f", // Border color on hover
    },
    "&.Mui-focused fieldset": {
      borderColor: "#d91a5f", // Border color when focused
    },
  },
};

const selectStyles = {
  padding: "2px 8px", // Adjust padding to reduce height
  "& .MuiSelect-select": {
    padding: "6px 10px", // Adjust padding for the select area
    lineHeight: "1.5", // Adjust line height
  },
};

const menuItemStyles = {
  padding: "8px 10px", // Adjust padding for MenuItems
};

const bucketList = [
  {
    key: "all",
    name: "All",
    color: "#f0f0f0",
    outlineColor: "#999999",
  },
  {
    key: "inProgress",
    name: "In-Progress",
    color: "#e2cef2",
    outlineColor: "#8b6ab2",
  },
  {
    key: "onHold",
    name: "On-Hold",
    color: "#f8d9bf",
    outlineColor: "#c47a43",
  },
  {
    key: "completed",
    name: "Completed",
    color: "#9cdce7",
    outlineColor: "#4c8fa1",
  },
];

const ListDictations = () => {
  const [dictationList, setDictationList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [uploadDictn, setUploadDict] = useState(false);
  const [listType, setListType] = useState("myList");
  const { CustomSnackbar, setSnackbarMessage } = useSnackbar();

  const status = useSelector((state) => state.dictation.status);
  const dos_from = useSelector((state) => state.dictation.dos_from);
  const dos_to = useSelector((state) => state.dictation.dos_to);

  const dispatch = useDispatch();
  const [bucket, setBucket] = useState({
    completed: 0,
    inProgress: 0,
    onHold: 0,
  });

  const userData = useUserData();
  const token = userData?.access_token;

  const fetchDictations = async ({ extraPayload, sendNpi = true } = {}) => {
    setLoading(true);
    setDictationList([]);
    try {
      const payload = {
        type: "Dictation",
        search_criteria: {
          practice_id: userData?.selected_practice,
          ...(sendNpi && { npi: userData.user_details.npi }),
          ...(extraPayload || {}),
          ...(status !== "All" && { status }),
        },
      };
      if (dos_from) {
        payload.search_criteria.dos_from = dayjs(dos_from).format("MM/DD/YYYY");
      }
      if (dos_to) {
        payload.search_criteria.dos_to = dayjs(dos_to).format("MM/DD/YYYY");
      }
      if (extraPayload) setIsSearched(true);
      const response = await getDictationList(payload, token);
      console.log("response>>>: ", response);
      if (response) setDictationList(response);
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    try {
      const payload = {
        type: "Dictation",
        search_criteria: {
          practice_id: userData?.selected_practice,
        },
      };
      const res = await getDictationStatusCounts(payload, token);
      if (res) {
        const data = res[0];
        setBucket({
          completed: data?.Total_Completed,
          inProgress: data?.Total_InProgress,
          onHold: data?.Total_OnHold,
        });
      }
    } catch (error) {
      console.log("error: ", error);
    }
  };

  useEffect(() => {
    fetchCounts();
    fetchDictations();
  }, [dos_from, dos_to, status]);

  const onChangeListType = (event) => {
    const selectedType = event.target.value;
    setListType(selectedType);
    if (selectedType === "myList") {
      fetchDictations();
    } else {
      fetchDictations({ sendNpi: false });
    }
  };

  const onClearDate = () => {
    dispatch(setDosFrom(null));
    dispatch(setDosTo(null));
  };

  return (
    <div className="dictations-list-page">
      <CustomSnackbar />
      <main>
        <Box
          display="flex"
          alignItems="center"
          gap={2}
          mb={4}
          flex={1}
          justifyContent={"center"}
        >
          {bucketList.map((item, index) => (
            <Box
              key={index}
              width={140}
              height={100}
              sx={{
                backgroundColor: item.color,
                outline:
                  item.name === status
                    ? `3px solid ${item.outlineColor}`
                    : "3px solid transparent",
                outlineOffset: "2px",
                cursor: "pointer",
              }}
              alignItems="center"
              justifyContent="center"
              display="flex"
              flexDirection="column"
              borderRadius={1}
              onClick={() => dispatch(setStatus(item.name))}
            >
              <Typography fontSize={16} fontWeight={600}>
                {item.name}
              </Typography>
              <Typography fontSize={14} fontWeight={500}>
                {bucket[item.key]}
              </Typography>
            </Box>
          ))}
        </Box>
        {/* Content based on active list */}
        <div className="header">
          <div className="btn-div">
            <Box display="flex" alignItems="center" gap={2}>
              {(dos_from || dos_to) && (
                <button
                  onClick={onClearDate}
                  className="blank-hover-btn"
                  style={{ marginLeft: 10 }}
                >
                  Clear
                </button>
              )}
              <DesktopDatePicker
                value={dayjs(dos_from)}
                onChange={(e) => dispatch(setDosFrom(e))}
                slotProps={{ textField: { variant: "standard" } }}
                format="MM/DD/YYYY"
                placeholder="MM/DD/YYYY"
                maxDate={dayjs()}
                label="DOS From"
              />
              <DesktopDatePicker
                value={dayjs(dos_to)}
                onChange={(e) => dispatch(setDosTo(e))}
                slotProps={{ textField: { variant: "standard" } }}
                format="MM/DD/YYYY"
                placeholder="MM/DD/YYYY"
                maxDate={dayjs()}
                label="DOS To"
              />
            </Box>
            <FormControl sx={formControlStyles}>
              <Select
                sx={selectStyles}
                value={listType}
                onChange={onChangeListType}
              >
                <MenuItem sx={menuItemStyles} value="myList">
                  Show My List
                </MenuItem>
                <MenuItem sx={menuItemStyles} value="practiceList">
                  Show Practice List
                </MenuItem>
              </Select>
            </FormControl>
            <button
              className="blank-hover-btn"
              onClick={() => setUploadDict(true)}
            >
              Upload Dictation
            </button>
            <button
              onClick={() => setShowSearchModal(true)}
              className="blank-hover-btn"
            >
              Advance Search
            </button>
          </div>
        </div>

        {isSearched && (
          <button
            onClick={() => {
              setIsSearched(false);
              fetchDictations();
            }}
            className="blank-hover-btn"
            style={{ marginLeft: 10 }}
          >
            Clear
          </button>
        )}
        {showSearchModal ? (
          <AdvanceSearchDictation
            onClose={() => setShowSearchModal(false)}
            onSearch={(payload) => fetchDictations({ extraPayload: payload })}
          />
        ) : null}
        {uploadDictn && (
          <UploadDictationModal
            onClose={() => setUploadDict(false)}
            setSnackbarMessage={setSnackbarMessage}
            fetchDictations={fetchDictations}
          />
        )}

        <DictationsList
          practice_id={userData?.selected_practice}
          records={dictationList}
          currentPlaying={currentPlaying}
          setCurrentPlaying={setCurrentPlaying}
          status="Test"
          loading={loading}
        />
      </main>
    </div>
  );
};

export default ListDictations;
